import { useEffect } from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LandingPage from "./pages/LandingPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import CreateBook from "./pages/CreateBook";
import BookDetail from "./pages/BookDetail";
import ContributePage from "./pages/ContributePage";
import OrderPage from "./pages/OrderPage";
import OrderTracking, { OrderSuccess } from "./pages/OrderTracking";
import AdminPanel from "./pages/AdminPanel";
import HelpPage from "./pages/HelpPage";
import SecuritySettingsPage from "./pages/SecuritySettingsPage";
import WaitlistPage from "./pages/WaitlistPage";
import HelpFloatingButton from "./components/HelpFloatingButton";
import LegalPage from "./pages/LegalPage";
import WelcomePage from "./pages/WelcomePage";
import CoPromotePage from "./pages/CoPromotePage";
import PaymentReturnPage from "./pages/PaymentReturnPage";
import FeedbackPage from "./pages/FeedbackPage";
import { PaymentPendingGate } from "./components/PaymentPendingGate";
import CookieNotice from "./components/CookieNotice";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // If user data passed from AuthCallback, skip auth check
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Pay-at-signup gate: block the app until the registration payment clears.
  if (user.payment_pending && !user.is_admin) {
    return <PaymentPendingGate />;
  }

  return children;
};

// App Router with session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment (not query params) for session_id synchronously
  // This MUST happen during render, not in useEffect, to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/lista-de-espera" element={<WaitlistPage />} />
      <Route path="/empezar" element={<WelcomePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/contribute/:inviteToken" element={<ContributePage />} />
      <Route path="/co-promote/:token" element={<CoPromotePage />} />
      <Route path="/feedback/:token" element={<FeedbackPage />} />
      <Route path="/pago/confirmacion" element={<PaymentReturnPage />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/books/new" element={
        <ProtectedRoute>
          <CreateBook />
        </ProtectedRoute>
      } />
      <Route path="/books/:bookId" element={
        <ProtectedRoute>
          <BookDetail />
        </ProtectedRoute>
      } />
      <Route path="/books/:bookId/order" element={
        <ProtectedRoute>
          <OrderPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          <OrderTracking />
        </ProtectedRoute>
      } />
      <Route path="/order/:orderId/success" element={
        <ProtectedRoute>
          <OrderSuccess />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/perfil" element={
        <ProtectedRoute>
          <SecuritySettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/account/security" element={
        <ProtectedRoute>
          <SecuritySettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/ayuda" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <HelpPage />
        </ProtectedRoute>
      } />

      {/* Legal pages (public, no auth) */}
      <Route path="/aviso-legal" element={<LegalPage />} />
      <Route path="/politica-de-privacidad" element={<LegalPage />} />
      <Route path="/politica-de-cookies" element={<LegalPage />} />
      <Route path="/terminos-y-condiciones" element={<LegalPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <HelpFloatingButton />
        <CookieNotice />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
